import React, { useState, useEffect, useMemo } from 'react';
import { dealerAPI, crmAPI, settingsAPI } from '../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, MessageCircle, CheckCircle, BellRing, RefreshCw, Settings as SettingsIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '@/components/ConfirmProvider';

// Predefined buckets in days. "Never" = no orders at all.
const BUCKETS = [
  { key: 'overdue', label: 'Overdue', color: '#C05C35' },
  { key: '7', label: '≤ 7 days', color: '#2B5339' },
  { key: '15', label: '8 – 15 days', color: '#3B7048' },
  { key: '30', label: '16 – 30 days', color: '#B08800' },
  { key: '45', label: '31 – 45 days', color: '#C05C35' },
  { key: '46+', label: '> 45 days', color: '#8B2F0E' },
  { key: 'never', label: 'No orders ever', color: '#5C6B61' },
];

// Call-mark tagging tool — team can flag/tag dealers for calls & reminders
// while scrolling the CRM. Persists per dealer (backend field: call_mark
// + optional call_on_date + note). Filterable via the mark chip strip.
export const CALL_MARKS = [
  { key: 'none',       label: 'No mark',    short: '—',        color: '#5C6B61', bg: 'transparent' },
  { key: 'call_today', label: 'Call today', short: 'Today',    color: 'white',   bg: '#C05C35' },
  { key: 'called',     label: 'Called',     short: 'Called',   color: 'white',   bg: '#2B5339' },
  { key: 'follow_up',  label: 'Follow-up',  short: 'Follow',   color: 'white',   bg: '#3B7048' },
  { key: 'call_on',    label: 'Call on…',   short: 'Call on',  color: 'white',   bg: '#B08800' },
];
export const markMetaFor = (key) => CALL_MARKS.find(m => m.key === (key || 'none')) || CALL_MARKS[0];

const bucketize = (days) => {
  if (days === null || days === undefined) return 'never';
  if (days <= 7) return '7';
  if (days <= 15) return '15';
  if (days <= 30) return '30';
  if (days <= 45) return '45';
  return '46+';
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtDateTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export const BillingCycleTab = ({ userRole, onDealerClick }) => {
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bucketFilter, setBucketFilter] = useState('overdue');
  const [search, setSearch] = useState('');
  // Call-mark filter — 'any' shows all rows regardless of mark. Set to one
  // of CALL_MARKS.key to narrow to that mark only.
  const [markFilter, setMarkFilter] = useState([]); // [] = any; multi-select of CALL_MARKS keys
  // Per-row inline edit state for the "Call on…" date picker popover.
  const [markDraftDate, setMarkDraftDate] = useState({}); // { [dealerId]: 'yyyy-mm-dd' }
  const [cycleDrafts, setCycleDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  // After saveCycleDays + fetchRows refreshes the table, this ref lets us
  // re-focus the next Days input so Excel-style "Enter to next row" actually
  // survives the row re-mount. Cleared after the effect fires.
  const focusNextIdxRef = React.useRef(null);
  // Bulk WhatsApp Reminder queue — mirrors the DealerCRM bulk WA pattern.
  // Operates on the CURRENTLY-FILTERED rows (bucket chip + search) so the
  // operator's intent matches the visible table. Browsers block opening
  // multiple tabs in a single click, so each row is sent one at a time
  // via "Send Next" or per-row "Send" buttons.
  const [bulkQueue, setBulkQueue] = useState(null); // { items: [...], sent: Set }
  // Admin-editable WhatsApp template. Loaded from /api/settings on mount;
  // a hardcoded fallback is used if the API is unreachable so the bulk
  // sender keeps working even in network-flaky scenarios.
  const FALLBACK_TEMPLATE = (
    'विकास इंडस्ट्रीज ( बिलिंग रिमाइंडर)\n\n' +
    'नमस्ते {dealer_name}\n' +
    'आपका आखरी बिल {last_dispatch_date} - {days_ago} दिन पहले को हुआ था।\n\n' +
    'कृपया पुनः सेवा का मौक़ा दे और बतायें की आपकी क्या डिमांड है एवं चित्रकूट पशु आहार आपको कब भिजवाना है।\n\n' +
    'धन्यवाद \uD83D\uDE4F\uD83C\uDFFC\n' +
    'चित्रकूट पशु आहार एवं पैलेट फीड्स'
  );
  const [template, setTemplate] = useState(FALLBACK_TEMPLATE);
  const [defaultTemplate, setDefaultTemplate] = useState(FALLBACK_TEMPLATE);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [templateDraft, setTemplateDraft] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await crmAPI.getDealers();
      setRows(res.data || []);
    } catch (e) {
      toast.error('Failed to load billing cycle data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, []);

  // After rows refresh (post-save), restore focus to the queued next-row Days
  // input so Enter-to-next-row keeps working through saves. The ref is set in
  // the Days input's onKeyDown handler before triggering the save.
  useEffect(() => {
    const nextIdx = focusNextIdxRef.current;
    if (nextIdx === null || nextIdx === undefined) return;
    focusNextIdxRef.current = null;
    // Run on next tick so React has finished committing the new DOM.
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-bc-cycle-row-idx="${nextIdx}"]`);
      if (el) {
        el.focus();
        if (typeof el.select === 'function') el.select();
      }
    });
  }, [rows]);

  // Load admin-editable WhatsApp template once on mount. Falls back to the
  // hardcoded default if the API is unreachable; never blocks the table.
  useEffect(() => {
    (async () => {
      try {
        const res = await settingsAPI.getBillingTemplate();
        if (res.data?.template) setTemplate(res.data.template);
        if (res.data?.default_template) setDefaultTemplate(res.data.default_template);
      } catch (e) {
        // Silent — fallback already in state.
      }
    })();
  }, []);

  const enriched = useMemo(() => {
    return rows.map(r => ({
      ...r,
      bucket: bucketize(r.days_since_dispatch),
      // Mark "due for next bill" when days_since_dispatch >= cycle, OR no dispatches ever.
      due_for_bill: (r.days_since_dispatch === null || r.days_since_dispatch === undefined)
        ? true
        : Number(r.days_since_dispatch) >= Number(r.billing_cycle_days || 15),
    }));
  }, [rows]);

  // Click-to-sort for Days Ago and Cycle (Days) columns. null = default
  // (days_since_dispatch desc, never-dispatched at bottom). Cycles per column:
  // null → asc → desc → null. Setting one clears the other.
  const [tableSort, setTableSort] = useState({ key: null, dir: null });
  const cycleColSort = (key) => {
    setTableSort((p) => {
      if (p.key !== key) return { key, dir: 'asc' };
      if (p.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: null };
    });
  };

  const filtered = useMemo(() => {
    let list = enriched;
    if (bucketFilter === 'overdue') {
      list = list.filter(r => r.due_for_bill);
    } else if (bucketFilter !== 'all') {
      list = list.filter(r => r.bucket === bucketFilter);
    }
    if (markFilter.length > 0) {
      list = list.filter(r => markFilter.includes(r.call_mark || 'none'));
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        (r.dealer_name || '').toLowerCase().includes(q) ||
        (r.town || '').toLowerCase().includes(q) ||
        (r.phone_number || '').toLowerCase().includes(q)
      );
    }
    // Sort: honors tableSort when set, otherwise defaults to days_since_dispatch
    // desc (most overdue first); never-dispatched rows go last.
    return [...list].sort((a, b) => {
      if (tableSort.key && tableSort.dir) {
        if (tableSort.key === 'days_ago') {
          const da = a.days_since_dispatch === null || a.days_since_dispatch === undefined ? Infinity : a.days_since_dispatch;
          const db2 = b.days_since_dispatch === null || b.days_since_dispatch === undefined ? Infinity : b.days_since_dispatch;
          // Never (Infinity) always at the bottom regardless of direction.
          if (da === Infinity && db2 !== Infinity) return 1;
          if (db2 === Infinity && da !== Infinity) return -1;
          if (da === Infinity && db2 === Infinity) return 0;
          return tableSort.dir === 'asc' ? da - db2 : db2 - da;
        }
        if (tableSort.key === 'cycle_days') {
          const av = Number(a.billing_cycle_days ?? 15);
          const bv = Number(b.billing_cycle_days ?? 15);
          if (av !== bv) return tableSort.dir === 'asc' ? av - bv : bv - av;
          // Tie-break on dealer name for stability.
          return (a.dealer_name || '').localeCompare(b.dealer_name || '', undefined, { sensitivity: 'base' });
        }
      }
      const da = a.days_since_dispatch === null || a.days_since_dispatch === undefined ? -1 : a.days_since_dispatch;
      const db2 = b.days_since_dispatch === null || b.days_since_dispatch === undefined ? -1 : b.days_since_dispatch;
      if (da === -1 && db2 !== -1) return 1;
      if (db2 === -1 && da !== -1) return -1;
      return db2 - da;
    });
  }, [enriched, bucketFilter, markFilter, search, tableSort]); // eslint-disable-line

  const counts = useMemo(() => {
    const m = { all: enriched.length, overdue: 0 };
    BUCKETS.forEach(b => { m[b.key] = 0; });
    enriched.forEach(r => {
      m[r.bucket] = (m[r.bucket] || 0) + 1;
      if (r.due_for_bill) m.overdue += 1;
    });
    return m;
  }, [enriched]);

  const markCounts = useMemo(() => {
    const m = { any: enriched.length };
    CALL_MARKS.forEach(cm => { m[cm.key] = 0; });
    enriched.forEach(r => {
      const k = r.call_mark || 'none';
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [enriched]);

  const setCallMark = async (dealer, mark, callOnDate = null, note = null) => {
    // Optimistic patch first — chip flips instantly. Backend reconciles via
    // the fetchRows() at the end; on failure we surface a toast and refresh
    // so the row snaps back to server truth.
    const optimistic = {
      call_mark: mark,
      call_on_date: mark === 'call_on' ? (callOnDate || null) : null,
      call_mark_note: note || '',
    };
    setRows(prev => prev.map(r => r.id === dealer.id ? { ...r, ...optimistic } : r));
    try {
      await crmAPI.setCallMark(dealer.id, {
        mark,
        call_on_date: mark === 'call_on' ? (callOnDate || null) : null,
        note: note || '',
      });
      toast.success(`${dealer.dealer_name}: ${markMetaFor(mark).label}`);
      // Silent fetch for server-derived fields (updated_at/by).
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save call mark');
      fetchRows();
    }
  };

  // ---- Bulk multi-select + mark ----
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkCallOn, setBulkCallOn] = useState('');

  const toggleSelect = (id) => setSelectedIds(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const allFilteredSelected = filtered.length > 0 && filtered.every(r => selectedIds.has(r.id));
  const toggleSelectAll = () => setSelectedIds(prev => {
    const n = new Set(prev);
    const allSel = filtered.length > 0 && filtered.every(r => n.has(r.id));
    if (allSel) filtered.forEach(r => n.delete(r.id));
    else filtered.forEach(r => n.add(r.id));
    return n;
  });

  const bulkSetMark = async (mark, callOnDate = null) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (mark === 'call_on' && !callOnDate) { toast.error('Pick a date for "Call on…"'); return; }
    setBulkBusy(true);
    // Optimistic patch so every selected chip flips instantly.
    setRows(prev => prev.map(r => ids.includes(r.id)
      ? { ...r, call_mark: mark, call_on_date: mark === 'call_on' ? (callOnDate || null) : null }
      : r));
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await crmAPI.setCallMark(id, { mark, call_on_date: mark === 'call_on' ? (callOnDate || null) : null, note: '' });
        ok++;
      } catch (e) { fail++; }
    }
    setBulkBusy(false);
    if (fail === 0) toast.success(`Marked ${ok} dealer${ok > 1 ? 's' : ''}: ${markMetaFor(mark).label}`);
    else toast.error(`${ok} saved, ${fail} failed — refreshing`);
    clearSelection();
    setBulkCallOn('');
    fetchRows();
  };

  const saveCycleDays = async (dealer, raw) => {
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 1 || n > 365) {
      toast.error('Enter a valid cycle (1 – 365 days)');
      return;
    }
    setSavingId(dealer.id);
    try {
      const res = await dealerAPI.update(dealer.id, { billing_cycle_days: n });
      toast.success(`${dealer.dealer_name} cycle set to ${n} days`);
      setCycleDrafts(prev => { const c = { ...prev }; delete c[dealer.id]; return c; });
      // Optimistic patch — splice the updated dealer into the local rows
      // so the "Cycle (days)" column reflects the save instantly and the
      // Enter-to-next-row focus restore can run without waiting for a full
      // /crm/dealers refetch. Background fetchRows() still reconciles any
      // server-derived fields (days_since_dispatch, due_for_bill, etc.).
      if (res?.data) {
        setRows(prev => prev.map(r => r.id === dealer.id ? { ...r, ...res.data } : r));
      }
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save cycle days');
    } finally {
      setSavingId(null);
    }
  };

  // Render the admin-editable WhatsApp template by substituting placeholders
  // with this dealer's data. Supported placeholders:
  //   {dealer_name}         — full dealer name
  //   {last_dispatch_date}  — DD/MM/YYYY (— if never dispatched)
  //   {days_ago}            — integer (0 if never dispatched)
  // Unknown placeholders are left untouched so they stand out during testing.
  const buildReminderText = (r) => {
    const days = r.days_since_dispatch ?? 0;
    const lastDate = r.last_dispatch ? fmtDate(r.last_dispatch) : '—';
    return (template || FALLBACK_TEMPLATE)
      .replace(/\{dealer_name\}/g, r.dealer_name || '')
      .replace(/\{last_dispatch_date\}/g, lastDate)
      .replace(/\{days_ago\}/g, String(days));
  };

  const stampReminder = async (dealer) => {
    try {
      await dealerAPI.markBillingReminder(dealer.id);
      // Optimistic local update
      setRows(prev => prev.map(r => r.id === dealer.id ? { ...r, last_billing_reminder_at: new Date().toISOString() } : r));
    } catch (e) {
      toast.error('Failed to record reminder');
    }
  };

  const sendWhatsApp = async (r) => {
    const raw = r.phone_number || '';
    const digits = String(raw).replace(/\D/g, '');
    if (!digits) { toast.error('No mobile number on file'); return; }
    const wa = digits.startsWith('91') && digits.length >= 12 ? digits : (digits.length === 10 ? `91${digits}` : digits);
    const url = `https://wa.me/${wa}?text=${encodeURIComponent(buildReminderText(r))}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    await stampReminder(r);
    toast.success('WhatsApp opened — reminder logged');
  };

  const sendSMS = async (r) => {
    // Twilio is mocked — show clear toast and stamp reminder anyway so admin's tracking stays accurate.
    if (!(await confirm({
      title: `Send SMS reminder to ${r.dealer_name}?`,
      description: `SMS will be queued via Twilio. Note: SMS integration is currently MOCKED — Twilio credentials not configured. The reminder will still be logged.`,
      confirmLabel: 'Send anyway',
    }))) return;
    await stampReminder(r);
    toast.success('SMS reminder logged (Twilio mocked)');
  };

  const markOnly = async (r) => {
    if (!(await confirm({
      title: `Mark reminder sent for ${r.dealer_name}?`,
      description: 'This will only stamp today as the reminder date — no message will be sent.',
      confirmLabel: 'Mark sent',
    }))) return;
    await stampReminder(r);
    toast.success('Reminder timestamp updated');
  };

  // Build the WhatsApp URL for a row (same logic as sendWhatsApp, but does
  // not navigate — used inside the bulk queue so we can open in a new tab
  // and stamp the reminder atomically per click.
  const buildWAUrl = (r) => {
    const raw = r.phone_number || '';
    const digits = String(raw).replace(/\D/g, '');
    if (!digits) return null;
    const wa = digits.startsWith('91') && digits.length >= 12 ? digits : (digits.length === 10 ? `91${digits}` : digits);
    return `https://wa.me/${wa}?text=${encodeURIComponent(buildReminderText(r))}`;
  };

  const openBulkReminders = () => {
    // Operate on the CURRENT visible table (bucket + search). Drop rows
    // without a phone number — they're not actionable on WhatsApp.
    const eligible = filtered.filter(r => !!r.phone_number);
    if (eligible.length === 0) {
      toast.info('No dealers with mobile numbers match the current filter');
      return;
    }
    setBulkQueue({ items: eligible, sent: new Set() });
  };

  const sendBulkItem = async (r) => {
    const url = buildWAUrl(r);
    if (!url) {
      toast.error(`${r.dealer_name}: no mobile number on file`);
      return;
    }
    // Open in a new tab. Browsers may block this if not in a user gesture —
    // but the user's click on "Send" IS a gesture, so popup blockers
    // generally allow it. Each row requires its own click.
    const win = window.open(url, '_blank');
    if (!win) {
      // Popup blocked — fall back to anchor click (still in-gesture).
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    }
    await stampReminder(r);
    setBulkQueue((prev) => {
      if (!prev) return prev;
      const next = new Set(prev.sent);
      next.add(r.id);
      return { ...prev, sent: next };
    });
  };

  const saveTemplate = async () => {
    const text = (templateDraft || '').trim();
    if (!text) { toast.error('Template cannot be empty'); return; }
    setTemplateSaving(true);
    try {
      const res = await settingsAPI.updateBillingTemplate(text);
      setTemplate(res.data?.template || text);
      toast.success('Template saved');
      setTemplateDialog(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    } finally {
      setTemplateSaving(false);
    }
  };

  const resetTemplate = async () => {
    if (!(await confirm({
      title: 'Reset to default template?',
      description: 'This will overwrite your custom template with the system default Hindi message.',
      confirmLabel: 'Reset',
    }))) return;
    setTemplateSaving(true);
    try {
      const res = await settingsAPI.resetBillingTemplate();
      const fresh = res.data?.template || defaultTemplate;
      setTemplate(fresh);
      setTemplateDraft(fresh);
      toast.success('Template reset to default');
    } catch (e) {
      toast.error('Failed to reset template');
    } finally {
      setTemplateSaving(false);
    }
  };

  // Live preview using the first visible row (or a synthetic sample) so the
  // admin sees exactly what dealers will receive.
  const previewSample = filtered[0] || { dealer_name: 'SAMPLE DEALER', last_dispatch: new Date().toISOString(), days_since_dispatch: 15 };
  const previewText = (() => {
    try {
      const days = previewSample.days_since_dispatch ?? 0;
      const lastDate = previewSample.last_dispatch ? fmtDate(previewSample.last_dispatch) : '—';
      return (templateDraft || '')
        .replace(/\{dealer_name\}/g, previewSample.dealer_name || '')
        .replace(/\{last_dispatch_date\}/g, lastDate)
        .replace(/\{days_ago\}/g, String(days));
    } catch (_) { return ''; }
  })();

  return (
    <div data-testid="billing-cycle-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#2B5339' }}>Billing Cycle</h2>
          <p className="text-xs mt-1" style={{ color: '#5C6B61' }}>
            Sorted by days since last dispatched/delivered order. Per-dealer cycle days are editable inline.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 self-start">
          {userRole === 'admin' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setTemplateDraft(template); setTemplateDialog(true); }}
              data-testid="bc-edit-template-btn"
              title="Edit the WhatsApp reminder template (admin only)"
            >
              <SettingsIcon className="w-4 h-4 mr-2" /> Edit Template
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={fetchRows} data-testid="bc-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Bulk WhatsApp action bar — counts dealers matching the current
          bucket + search filter so the operator's intent matches the visible
          table. Hidden when nothing actionable to keep the header clean. */}
      {(() => {
        const eligibleCount = filtered.filter(r => !!r.phone_number).length;
        if (eligibleCount === 0) return null;
        return (
          <div className="flex justify-end mb-3">
            <Button
              size="sm"
              onClick={openBulkReminders}
              className="text-xs font-semibold"
              style={{ background: '#25D366', color: 'white' }}
              title={`Send WhatsApp reminders to all ${eligibleCount} dealer(s) in the current filter`}
              data-testid="bc-bulk-wa-btn"
            >
              <MessageCircle className="w-3 h-3 mr-1" /> Bulk WhatsApp ({eligibleCount})
            </Button>
          </div>
        );
      })()}

      {/* Bucket filter chips */}
      <Card className="p-4 bg-white border mb-5" style={{ borderColor: '#E2DFD8' }}>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button
            type="button"
            onClick={() => setBucketFilter('overdue')}
            className="text-xs px-3 py-1.5 rounded-full font-medium border transition-colors"
            style={bucketFilter === 'overdue' ? { background: '#C05C35', color: 'white', borderColor: '#C05C35' } : { background: 'white', color: '#C05C35', borderColor: '#C05C35' }}
            data-testid="bc-bucket-overdue"
          >
            <BellRing className="w-3 h-3 inline-block mr-1 -mt-0.5" />
            Due for Bill <span className="ml-1 font-bold">{counts.overdue || 0}</span>
          </button>
          <button
            type="button"
            onClick={() => setBucketFilter('all')}
            className="text-xs px-3 py-1.5 rounded-full font-medium border transition-colors"
            style={bucketFilter === 'all' ? { background: '#2B5339', color: 'white', borderColor: '#2B5339' } : { background: 'white', color: '#2B5339', borderColor: '#E2DFD8' }}
            data-testid="bc-bucket-all"
          >
            All <span className="ml-1 font-bold">{counts.all}</span>
          </button>
          {BUCKETS.filter(b => b.key !== 'overdue').map(b => (
            <button
              key={b.key}
              type="button"
              onClick={() => setBucketFilter(b.key)}
              className="text-xs px-3 py-1.5 rounded-full font-medium border transition-colors"
              style={bucketFilter === b.key
                ? { background: b.color, color: 'white', borderColor: b.color }
                : { background: 'white', color: b.color, borderColor: b.color }}
              data-testid={`bc-bucket-${b.key}`}
            >
              {b.label} <span className="ml-1 font-bold">{counts[b.key] || 0}</span>
            </button>
          ))}
        </div>
        {/* Call-mark filter strip — narrows the visible rows to a specific
            call-mark. Kept beneath the bucket chips so operators can layer
            both filters (e.g. Overdue + Call Today). */}
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="bc-mark-filter-strip">
          <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#5C6B61' }}>Mark:</span>
          <button
            type="button"
            onClick={() => setMarkFilter([])}
            className="text-xs px-3 py-1 rounded-full font-medium border transition-colors"
            style={markFilter.length === 0
              ? { background: '#1F1F1F', color: 'white', borderColor: '#1F1F1F' }
              : { background: 'white', color: '#1F1F1F', borderColor: '#E2DFD8' }}
            data-testid="bc-mark-filter-any"
          >
            Any <span className="ml-1 font-bold">{markCounts.any || 0}</span>
          </button>
          {CALL_MARKS.map(cm => {
            const active = markFilter.includes(cm.key);
            return (
            <button
              key={cm.key}
              type="button"
              onClick={() => setMarkFilter(prev => prev.includes(cm.key) ? prev.filter(k => k !== cm.key) : [...prev, cm.key])}
              className="text-xs px-3 py-1 rounded-full font-medium border transition-colors"
              style={active
                ? { background: cm.bg === 'transparent' ? '#5C6B61' : cm.bg, color: 'white', borderColor: cm.bg === 'transparent' ? '#5C6B61' : cm.bg }
                : { background: 'white', color: cm.bg === 'transparent' ? '#5C6B61' : cm.bg, borderColor: cm.bg === 'transparent' ? '#B9BEB6' : cm.bg }}
              data-testid={`bc-mark-filter-${cm.key}`}
            >
              {cm.label} <span className="ml-1 font-bold">{markCounts[cm.key] || 0}</span>
            </button>
          );})}
        </div>
        <Input
          type="search"
          placeholder="Search by name, town or mobile..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
          data-testid="bc-search-input"
        />
      </Card>

      {/* Bulk multi-select action bar — appears when rows are selected */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 p-3 rounded-lg border" style={{ background: '#FBF9F6', borderColor: '#E2DFD8' }} data-testid="bc-bulk-bar">
          <span className="text-sm font-semibold" style={{ color: '#2B5339' }} data-testid="bc-bulk-count">{selectedIds.size} selected</span>
          <span className="text-xs" style={{ color: '#5C6B61' }}>Mark all as:</span>
          {CALL_MARKS.filter(cm => cm.key !== 'call_on').map(cm => (
            <Button
              key={cm.key}
              size="sm"
              variant="outline"
              disabled={bulkBusy}
              onClick={() => bulkSetMark(cm.key)}
              data-testid={`bc-bulk-mark-${cm.key}`}
              style={cm.bg === 'transparent'
                ? { color: '#5C6B61', borderColor: '#B9BEB6' }
                : { color: cm.bg, borderColor: cm.bg }}
            >
              {cm.label}
            </Button>
          ))}
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" disabled={bulkBusy} data-testid="bc-bulk-mark-call_on" style={{ color: '#B08800', borderColor: '#B08800' }}>Call on…</Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="start">
              <Input type="date" value={bulkCallOn} onChange={(e) => setBulkCallOn(e.target.value)} data-testid="bc-bulk-callon-date" />
              <Button size="sm" className="w-full mt-2 text-white" style={{ background: '#B08800' }} disabled={!bulkCallOn || bulkBusy} onClick={() => bulkSetMark('call_on', bulkCallOn)} data-testid="bc-bulk-callon-apply">Apply to {selectedIds.size}</Button>
            </PopoverContent>
          </Popover>
          <div className="flex-1" />
          {bulkBusy && <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#2B5339' }} />}
          <Button size="sm" variant="ghost" onClick={clearSelection} disabled={bulkBusy} data-testid="bc-bulk-clear">Clear</Button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2B5339' }} />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center" style={{ borderColor: '#E2DFD8' }}>
          <p style={{ color: '#5C6B61' }}>No dealers in this bucket.</p>
        </Card>
      ) : (
        <Card className="bg-white border overflow-hidden" style={{ borderColor: '#E2DFD8' }}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead>
                <tr style={{ background: '#FBF9F6' }}>
                  <th className="p-3 w-10 text-center">
                    <Checkbox checked={allFilteredSelected} onCheckedChange={toggleSelectAll} aria-label="Select all" data-testid="bc-select-all" />
                  </th>
                  <th className="text-left p-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>S.No</th>
                  <th className="text-left p-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>Dealer</th>
                  <th className="text-left p-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>Mark</th>
                  <th className="text-left p-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>Mobile</th>
                  <th className="text-center p-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>Last Dispatch</th>
                  <th
                    className="text-center p-3 text-xs font-semibold whitespace-nowrap cursor-pointer select-none hover:bg-white"
                    style={{ color: '#5C6B61' }}
                    onClick={() => cycleColSort('days_ago')}
                    data-testid="bc-sort-days-ago"
                    title="Click to sort by Days Ago (asc → desc → default)"
                  >
                    Days Ago{tableSort.key === 'days_ago' ? (tableSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                  </th>
                  <th
                    className="text-center p-3 text-xs font-semibold whitespace-nowrap cursor-pointer select-none hover:bg-white"
                    style={{ color: '#5C6B61' }}
                    onClick={() => cycleColSort('cycle_days')}
                    data-testid="bc-sort-cycle-days"
                    title="Click to sort by Cycle (days) (asc → desc → default)"
                  >
                    Cycle (days){tableSort.key === 'cycle_days' ? (tableSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                  </th>
                  <th className="text-right p-3 text-xs font-semibold whitespace-nowrap" style={{ color: '#5C6B61' }}>Outstanding</th>
                  <th className="text-center p-3 text-xs font-semibold whitespace-nowrap" style={{ color: '#5C6B61' }}>Last Reminder</th>
                  <th className="text-center p-3 text-xs font-semibold" style={{ color: '#5C6B61' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => {
                  const bucketObj = BUCKETS.find(b => b.key === r.bucket) || BUCKETS[BUCKETS.length - 1];
                  const draftCycle = cycleDrafts[r.id] !== undefined ? cycleDrafts[r.id] : (r.billing_cycle_days ?? 15);
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #E2DFD8', background: selectedIds.has(r.id) ? '#EEF3EC' : (idx % 2 === 0 ? '#FFFFFF' : '#FBF9F6') }} className="hover:bg-[#F5F1E8] transition-colors" data-testid="bc-row">
                      <td className="p-3 text-center">
                        <Checkbox checked={selectedIds.has(r.id)} onCheckedChange={() => toggleSelect(r.id)} aria-label={`Select ${r.dealer_name}`} data-testid="bc-row-select" />
                      </td>
                      <td className="p-3 text-sm">{idx + 1}</td>
                      <td className="p-3 text-sm">
                        {onDealerClick ? (
                          <button
                            type="button"
                            onClick={() => onDealerClick(r)}
                            className="text-left hover:underline focus:outline-none"
                            data-testid="bc-dealer-name-link"
                          >
                            <div className="font-medium" style={{ color: '#2B5339' }}>{r.dealer_name}</div>
                            <div className="text-xs" style={{ color: '#5C6B61' }}>{r.town || '-'}</div>
                          </button>
                        ) : (
                          <>
                            <div className="font-medium" style={{ color: '#2B5339' }}>{r.dealer_name}</div>
                            <div className="text-xs" style={{ color: '#5C6B61' }}>{r.town || '-'}</div>
                          </>
                        )}
                        {r.due_for_bill && (
                          <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: '#C05C35', color: 'white' }} data-testid="bc-due-badge">
                            Due
                          </span>
                        )}
                      </td>
                      {/* Call-mark chip: click to change. Popover exposes the
                          full option list + optional date for "Call on…". */}
                      <td className="p-3 text-sm">
                        {(() => {
                          const meta = markMetaFor(r.call_mark);
                          const isCallOn = (r.call_mark === 'call_on');
                          const draftDate = markDraftDate[r.id] !== undefined ? markDraftDate[r.id] : (r.call_on_date || '');
                          return (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors"
                                  style={meta.bg === 'transparent'
                                    ? { color: '#5C6B61', borderColor: '#E2DFD8', background: 'white' }
                                    : { color: meta.color, background: meta.bg, borderColor: meta.bg }}
                                  title={isCallOn && r.call_on_date ? `Call on ${r.call_on_date}` : meta.label}
                                  data-testid="bc-mark-chip"
                                >
                                  {meta.short}
                                  {isCallOn && r.call_on_date && (
                                    <span className="opacity-90">· {r.call_on_date}</span>
                                  )}
                                  <span style={{ marginLeft: 2, fontSize: 9, opacity: 0.75 }}>▾</span>
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="start" className="w-56 p-0" data-testid="bc-mark-popover">
                                <div className="px-3 py-2 border-b" style={{ borderColor: '#E2DFD8', background: '#FBF9F6' }}>
                                  <div className="text-xs font-semibold" style={{ color: '#2B5339' }}>Set call mark</div>
                                  {r.call_mark_updated_at && (
                                    <div className="text-[10px]" style={{ color: '#5C6B61' }}>
                                      Last: {fmtDateTime(r.call_mark_updated_at)}{r.call_mark_updated_by ? ` · ${r.call_mark_updated_by}` : ''}
                                    </div>
                                  )}
                                </div>
                                <div className="p-1">
                                  {CALL_MARKS.map(cm => (
                                    <button
                                      key={cm.key}
                                      type="button"
                                      onClick={() => {
                                        if (cm.key === 'call_on') return; // handled by inline picker below
                                        setCallMark(r, cm.key);
                                      }}
                                      className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-[#FBF9F6] text-left"
                                      data-testid={`bc-mark-option-${cm.key}`}
                                    >
                                      <span
                                        className="inline-flex items-center gap-2 text-xs"
                                        style={{ color: cm.bg === 'transparent' ? '#5C6B61' : cm.bg }}
                                      >
                                        <span
                                          className="inline-block w-2.5 h-2.5 rounded-full"
                                          style={{ background: cm.bg === 'transparent' ? '#B9BEB6' : cm.bg }}
                                        />
                                        {cm.label}
                                      </span>
                                      {(r.call_mark || 'none') === cm.key && (
                                        <CheckCircle className="w-3 h-3" style={{ color: '#2B5339' }} />
                                      )}
                                    </button>
                                  ))}
                                  <div className="px-2 pt-2 mt-1 border-t" style={{ borderColor: '#E2DFD8' }}>
                                    <label className="block text-[10px] font-semibold mb-1" style={{ color: '#5C6B61' }}>
                                      Call on…
                                    </label>
                                    <div className="flex items-center gap-1">
                                      <Input
                                        type="date"
                                        value={draftDate}
                                        onChange={(e) => setMarkDraftDate(prev => ({ ...prev, [r.id]: e.target.value }))}
                                        className="h-7 text-xs"
                                        data-testid="bc-mark-call-on-date"
                                      />
                                      <Button
                                        size="sm"
                                        className="h-7 text-[10px] px-2"
                                        style={{ background: '#B08800', color: 'white' }}
                                        disabled={!draftDate}
                                        onClick={() => setCallMark(r, 'call_on', draftDate)}
                                        data-testid="bc-mark-call-on-save"
                                      >Set</Button>
                                    </div>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          );
                        })()}
                      </td>
                      <td className="p-3 text-sm" style={{ color: '#5C6B61' }}>{r.phone_number || '-'}</td>
                      <td className="p-3 text-sm text-center">{fmtDate(r.last_dispatch)}</td>
                      <td className="p-3 text-sm text-center">
                        {r.days_since_dispatch === null || r.days_since_dispatch === undefined ? (
                          <span className="text-xs font-medium" style={{ color: bucketObj.color }}>Never</span>
                        ) : (
                          <span className="font-bold" style={{ color: bucketObj.color }}>{r.days_since_dispatch}</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Input
                            type="number"
                            min="1"
                            max="365"
                            value={draftCycle}
                            onChange={(e) => setCycleDrafts(prev => ({ ...prev, [r.id]: e.target.value }))}
                            onBlur={(e) => {
                              const newVal = e.target.value;
                              if (Number(newVal) !== Number(r.billing_cycle_days)) saveCycleDays(r, newVal);
                            }}
                            onKeyDown={(e) => {
                              // Enter → save + jump focus to the NEXT row's Days
                              // input. Mirrors Excel-style tabular data entry.
                              // We queue the next-row index via focusNextIdxRef
                              // BEFORE the blur (which fires saveCycleDays →
                              // fetchRows → row re-mount). A `useEffect [rows]`
                              // picks the queued index up and re-focuses after
                              // the new DOM is committed.
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const valChanged = Number(e.target.value) !== Number(r.billing_cycle_days);
                                if (valChanged) {
                                  focusNextIdxRef.current = idx + 1;
                                  // Trigger save via blur (existing onBlur path).
                                  e.target.blur();
                                } else {
                                  // No change — just move focus immediately.
                                  const next = document.querySelector(`[data-bc-cycle-row-idx="${idx + 1}"]`);
                                  if (next) {
                                    next.focus();
                                    if (typeof next.select === 'function') next.select();
                                  } else {
                                    e.target.blur();
                                  }
                                }
                              }
                            }}
                            className="h-8 w-20 text-center text-sm"
                            disabled={userRole !== 'admin' || savingId === r.id}
                            data-testid="bc-cycle-input"
                            data-bc-cycle-row-idx={idx}
                          />
                          {savingId === r.id && <Loader2 className="w-3 h-3 animate-spin" />}
                        </div>
                      </td>
                      <td className="p-3 text-sm text-right font-medium whitespace-nowrap">
                        {Number(r.outstanding_total) > 0 ? (
                          <span style={{ color: '#C05C35' }}>{'\u20B9'}{Number(r.outstanding_total).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                        ) : (
                          <span style={{ color: '#B9BEB6' }}>-</span>
                        )}
                      </td>
                      <td className="p-3 text-xs text-center" style={{ color: '#5C6B61' }} data-testid="bc-last-reminder">
                        {r.last_billing_reminder_at ? fmtDateTime(r.last_billing_reminder_at) : <span style={{ color: '#B9BEB6' }}>—</span>}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1 justify-center">
                          <Button
                            size="sm"
                            className="text-xs px-2 py-1 h-7"
                            style={{ background: '#25D366', color: 'white', fontSize: '11px' }}
                            onClick={() => sendWhatsApp(r)}
                            title="Send WhatsApp reminder"
                            data-testid="bc-whatsapp-btn"
                          >
                            <MessageCircle className="w-3 h-3 mr-1" /> WA
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs px-2 py-1 h-7"
                            style={{ fontSize: '11px' }}
                            onClick={() => sendSMS(r)}
                            title="Send SMS reminder (Twilio mocked)"
                            data-testid="bc-sms-btn"
                          >
                            SMS
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs px-2 py-1 h-7"
                            style={{ fontSize: '11px', color: '#2B5339', borderColor: '#2B5339' }}
                            onClick={() => markOnly(r)}
                            title="Just mark reminder sent (no message)"
                            data-testid="bc-mark-btn"
                          >
                            <CheckCircle className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Edit Template dialog — admin-only. Placeholders are listed inline
          for discoverability; a live preview shows exactly what the next
          dealer in the visible list will receive. */}
      <Dialog open={templateDialog} onOpenChange={setTemplateDialog}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="bc-template-dialog">
          <DialogHeader>
            <DialogTitle>Edit WhatsApp Reminder Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs p-2 rounded" style={{ background: '#FBF9F6', color: '#5C6B61', border: '1px solid #E2DFD8' }}>
              <span className="font-semibold" style={{ color: '#2B5339' }}>Placeholders</span> (auto-filled per dealer at send time):
              <code className="ml-1 px-1.5 py-0.5 rounded" style={{ background: '#FFF8F2', color: '#C05C35' }}>{'{dealer_name}'}</code>{' '}
              <code className="px-1.5 py-0.5 rounded" style={{ background: '#FFF8F2', color: '#C05C35' }}>{'{last_dispatch_date}'}</code>{' '}
              <code className="px-1.5 py-0.5 rounded" style={{ background: '#FFF8F2', color: '#C05C35' }}>{'{days_ago}'}</code>
            </div>
            <textarea
              value={templateDraft}
              onChange={(e) => setTemplateDraft(e.target.value)}
              className="w-full min-h-[200px] p-3 rounded border text-sm font-mono"
              style={{ borderColor: '#E2DFD8' }}
              data-testid="bc-template-textarea"
              placeholder="Type your WhatsApp reminder message..."
            />
            <div>
              <div className="text-xs font-semibold mb-1" style={{ color: '#2B5339' }}>Live Preview <span className="font-normal" style={{ color: '#5C6B61' }}>(using {previewSample.dealer_name})</span></div>
              <div
                className="p-3 rounded text-sm whitespace-pre-wrap"
                style={{ background: '#DCF8C6', border: '1px solid #C5E1A5', color: '#1B1B1B' }}
                data-testid="bc-template-preview"
              >
                {previewText || <span style={{ color: '#9AA0A6' }}>Preview will appear here as you type.</span>}
              </div>
            </div>
            <div className="flex flex-wrap justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={resetTemplate}
                disabled={templateSaving}
                data-testid="bc-template-reset-btn"
                style={{ color: '#C05C35', borderColor: '#C05C35' }}
              >
                Reset to Default
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setTemplateDialog(false)} disabled={templateSaving} data-testid="bc-template-cancel-btn">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={saveTemplate}
                  disabled={templateSaving}
                  data-testid="bc-template-save-btn"
                  style={{ background: '#2B5339', color: 'white' }}
                >
                  {templateSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Save Template
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk WhatsApp Reminder queue — operator clicks rows one-by-one
          (browser popup blockers stop us from opening many tabs at once).
          Each click stamps the reminder timestamp + marks the row done. */}
      <Dialog open={!!bulkQueue} onOpenChange={(o) => { if (!o) setBulkQueue(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bc-bulk-wa-dialog">
          <DialogHeader>
            <DialogTitle>
              Bulk WhatsApp Reminders — {bulkQueue?.items?.length || 0} dealer{(bulkQueue?.items?.length || 0) === 1 ? '' : 's'}
            </DialogTitle>
          </DialogHeader>
          {bulkQueue && (() => {
            const sentCount = bulkQueue.sent.size;
            const total = bulkQueue.items.length;
            const pending = bulkQueue.items.filter(r => !bulkQueue.sent.has(r.id));
            const nextRow = pending[0];
            return (
              <div className="space-y-4">
                <div className="text-sm" style={{ color: '#5C6B61' }}>
                  Browsers block auto-opening multiple tabs, so each reminder opens on its own click. Click <strong>Send Next</strong> to go top-to-bottom — most overdue first.
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm">
                    <span className="font-bold" style={{ color: '#2B5339' }}>{sentCount}</span> / {total} sent
                  </div>
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: '#E2DFD8' }}>
                    <div className="h-full transition-all" style={{ width: `${total ? (sentCount / total) * 100 : 0}%`, background: '#25D366' }} />
                  </div>
                  {nextRow && (
                    <Button
                      size="sm"
                      onClick={() => sendBulkItem(nextRow)}
                      data-testid="bc-bulk-wa-send-next-btn"
                      style={{ background: '#25D366', color: 'white' }}
                    >
                      <MessageCircle className="w-3 h-3 mr-1" /> Send Next
                    </Button>
                  )}
                </div>
                <table className="w-full text-sm border" data-testid="bc-bulk-wa-table">
                  <thead style={{ background: '#FBF9F6' }}>
                    <tr>
                      <th className="p-2 text-left">Dealer</th>
                      <th className="p-2 text-right whitespace-nowrap">Days Ago</th>
                      <th className="p-2 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkQueue.items.map((r) => {
                      const done = bulkQueue.sent.has(r.id);
                      return (
                        <tr key={r.id} className="border-t" style={{ borderColor: '#E2DFD8', opacity: done ? 0.55 : 1 }}>
                          <td className="p-2">
                            <div className="font-medium">{r.dealer_name}</div>
                            <div className="text-[10px]" style={{ color: '#5C6B61' }}>{r.town || ''} · {r.phone_number}</div>
                          </td>
                          <td className="p-2 text-right" style={{ color: '#C05C35' }}>
                            {r.days_since_dispatch === null || r.days_since_dispatch === undefined ? 'Never' : `${r.days_since_dispatch}d`}
                          </td>
                          <td className="p-2 text-center">
                            {done ? (
                              <span className="text-xs font-semibold" style={{ color: '#2B5339' }}>✓ Sent</span>
                            ) : (
                              <button
                                onClick={() => sendBulkItem(r)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs hover:opacity-80"
                                style={{ background: '#25D366', color: 'white' }}
                                data-testid="bc-bulk-wa-row-send-btn"
                              >
                                <MessageCircle className="w-3 h-3" /> Send
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={() => setBulkQueue(null)} data-testid="bc-bulk-wa-close-btn">
                    {sentCount === total && total > 0 ? 'Done' : 'Close'}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};
